import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Button,
  Input,
  Label,
  RichTextEditor,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@org/ui';
import type { RichTextDoc } from '@org/model';
import { useState } from 'react';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { useMentionSearch } from '@/hooks/use-mention-search';
import { useRichTextMediaUpload } from '@/hooks/use-rich-text-media-upload';
import { EMPTY_DOC, isDocEmpty } from '@/utils/rich-text-empty';

type NewPostSearch = {
  type?: 'discussion' | 'poll';
  spaceId?: string;
};

export const Route = createFileRoute('/posts/new')({
  component: NewPostPage,
  validateSearch: (search: Record<string, unknown>): NewPostSearch => ({
    type: (search.type as NewPostSearch['type']) || undefined,
    spaceId: search.spaceId as string | undefined,
  }),
});

function NewPostPage() {
  const { t } = useTranslation('community');
  const navigate = useNavigate();
  const { toast } = useToast();
  const search = Route.useSearch();

  const [title, setTitle] = useState('');
  const [contentDoc, setContentDoc] = useState<RichTextDoc>(EMPTY_DOC);
  const [spaceId, setSpaceId] = useState(search.spaceId ?? '');
  const [type, setType] = useState<'discussion' | 'poll'>(search.type ?? 'discussion');

  const searchUsers = useMentionSearch();
  const uploadMedia = useRichTextMediaUpload();
  const contentEmpty = isDocEmpty(contentDoc);

  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);

  const addPollOption = () => {
    if (pollOptions.length < 5) {
      setPollOptions([...pollOptions, '']);
    }
  };

  const removePollOption = (index: number) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index));
    }
  };

  const updatePollOption = (index: number, value: string) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  const { data: spacesData } = communityTrpc.spaces.list.useQuery({ limit: 50 });
  const spaces = spacesData?.spaces ?? [];

  const hasPreselectedSpace = !!search.spaceId;

  const createPost = communityTrpc.posts.create.useMutation({
    onSuccess: (post) => {
      toast({
        title: t('newPost.publishSuccess'),
        description: t('newPost.publishSuccessDescription'),
      });
      navigate({ to: `/posts/${post.postId}` });
    },
    onError: (error) => {
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const submitForm = () => {
    if (!title || contentEmpty || !spaceId) return;

    if (type === 'poll') {
      const validOptions = pollOptions.filter((opt) => opt.trim().length > 0);
      if (validOptions.length < 2) {
        toast({
          title: t('common.error'),
          description: t('newPost.pollMinOptions'),
          variant: 'destructive',
        });
        return;
      }
      createPost.mutate({
        spaceId,
        type,
        title,
        content: contentDoc,
        pollQuestion: title,
        pollOptions: validOptions,
      });
    } else {
      createPost.mutate({
        spaceId,
        type,
        title,
        content: contentDoc,
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitForm();
  };

  const pageTitle = type === 'poll' ? t('newPost.titlePoll') : t('newPost.titleDiscussion');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/">
          <Button variant="ghost" size="icon" aria-label={t('common.back')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">{pageTitle}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 rounded-2xl border border-border bg-card p-6">
        <div className="space-y-2">
          <Label htmlFor="space">{t('newPost.spaceLabel')}</Label>
          <Select value={spaceId} onValueChange={setSpaceId} disabled={hasPreselectedSpace}>
            <SelectTrigger>
              <SelectValue placeholder={t('newPost.spacePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {spaces.map((space) => (
                <SelectItem key={space.spaceId} value={space.spaceId}>
                  {space.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!search.type && (
          <div className="space-y-2">
            <Label htmlFor="type">{t('newPost.typeLabel')}</Label>
            <Select value={type} onValueChange={(v) => setType(v as typeof type)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="discussion">{t('newPost.typeDiscussion')}</SelectItem>
                <SelectItem value="poll">{t('newPost.typePoll')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="title">{t('newPost.titleLabel')}</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('newPost.titlePlaceholder')}
            maxLength={200}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="content">{t('newPost.contentLabel')}</Label>
          <RichTextEditor
            value={contentDoc}
            onChange={setContentDoc}
            onSubmit={submitForm}
            searchUsers={searchUsers}
            uploadMedia={uploadMedia}
            placeholder={
              type === 'poll'
                ? t('newPost.contentPlaceholderPoll')
                : t('newPost.contentPlaceholderDiscussion')
            }
            hint={t('editor.hint')}
            minHeight={200}
          />
        </div>

        {type === 'poll' && (
          <div className="space-y-3">
            <Label>{t('newPost.pollOptionsLabel')}</Label>
            {pollOptions.map((option, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={option}
                  onChange={(e) => updatePollOption(index, e.target.value)}
                  placeholder={t('newPost.pollOptionPlaceholder', { number: index + 1 })}
                  maxLength={100}
                />
                {pollOptions.length > 2 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removePollOption(index)}
                    aria-label={t('common.delete')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {pollOptions.length < 5 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addPollOption}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                {t('newPost.addOption')}
              </Button>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Link to="/">
            <Button type="button" variant="outline">
              {t('common.cancel')}
            </Button>
          </Link>
          <Button
            type="submit"
            disabled={
              !title ||
              contentEmpty ||
              !spaceId ||
              createPost.isPending ||
              (type === 'poll' && pollOptions.filter((o) => o.trim()).length < 2)
            }
          >
            {createPost.isPending ? t('newPost.publishing') : t('newPost.publish')}
          </Button>
        </div>
      </form>
    </div>
  );
}
